<template>
  <div class="
  opener
  h-[60vh]
  md:h-[500px]
">
    <div class="page-wrapper
  h-[60vh]
  md:h-[500px]
">
      <div class="slider-container relative h-full flex flex-col justify-center">
        <!-- Slider Content -->
        <div class="slider flex pt-2">
          <!-- Mobile: Show 1 card -->
          <router-link
            v-if="currentImages.length > 0"
            :to="`/bildergalerie/${currentImages[0].image_id}`"
            class="slider-card-mobile md:hidden"
          >
            <div class="slider-card-image-mobile">
              <img
                :src="`https://eisenbahn.wingen.de${currentImages[0].path}`"
                :alt="currentImages[0].title"
              />
            </div>
            <div class="slider-card-content">
              <h3 class="slider-card-title">
                {{ currentImages[0].title }}
              </h3>
              <p class="slider-card-description-mobile">
                {{ currentImages[0].description }}
              </p>
              <span class="slider-card-button">
                zum Bild
              </span>
            </div>
          </router-link>

          <!-- Desktop: Show 2 cards side by side -->
          <div class="slider-desktop hidden md:flex w-full justify-center gap-6">
            <router-link
              v-for="image in currentImages"
              :key="image.image_id"
              :to="`/bildergalerie/${image.image_id}`"
              class="slider-card group"
            >
              <!-- Image Container -->
              <div class="slider-card-image">
                <img
                  :src="`https://eisenbahn.wingen.de${image.path}`"
                  :alt="image.title"
                  class="w-full h-full object-contain"
                />
              </div>
              <!-- Text Container -->
              <div class="slider-card-content">
                <h3 class="slider-card-title">
                  {{ image.title }}
                </h3>
                <p class="slider-card-description">
                  {{ image.description }}
                </p>
                <span class="slider-card-button">
                  zum Bild
                </span>
              </div>
            </router-link>
          </div>
        </div>

        <!-- Navigation Arrows -->
        <button
          v-if="latestImages.length > 2"
          @click="prevSlide"
          class="slider-nav slider-nav--prev"
          aria-label="Vorherige Bilder"
        >
          <svg width="24" height="24" fill="currentColor" viewBox="0 0 24 24">
            <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/>
          </svg>
        </button>
        <button
          v-if="latestImages.length > 2"
          @click="nextSlide"
          class="slider-nav slider-nav--next"
          aria-label="Nächste Bilder"
        >
          <svg width="24" height="24" fill="currentColor" viewBox="0 0 24 24">
            <path d="M8.59 16.59L10 18l6-6-6-6-1.41 1.41L13.17 12z"/>
          </svg>
        </button>

        <!-- Dots Navigation -->
        <div v-if="totalSlides > 1" class="slider-dots">
          <button
            v-for="(_, index) in totalSlides"
            :key="index"
            @click="goToSlide(index)"
            class="slider-dot"
            :class="{ 'slider-dot--active': currentSlide === index }"
            :aria-label="`Slide ${index + 1}`"
          />
        </div>
      </div>
    </div>
  </div>
  <div class="page-wrapper">
    <div v-if="articles.length">
      <div class="text-2xl text-blue font-bold my-2 text-left">
        Neues von der Eisenbahnstiftung
      </div>
      <div class="md:flex">
        <news-item v-for="article in articles" :key="article.ID" :article="article"></news-item>
      </div>
      <router-link to="/news" class="text-orange font-bold underline text-right block mt-1 mb-2">
        Alle Nachrichten anzeigen
      </router-link>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import axios from 'axios';
import { API_ENDPOINTS } from '@/lib/api';
import NewsItem from "@/components/home/newsItem.vue";

const articles = ref([]);
const latestImages = ref([]);
const currentSlide = ref(0);
const autoplayInterval = ref(null);
const AUTOPLAY_DELAY = 5000; // 5 seconds

// Computed: Total number of slides (2 images per slide on desktop)
const totalSlides = computed(() => {
  return Math.ceil(latestImages.value.length / 2);
});

// Computed: Current 2 images to display
const currentImages = computed(() => {
  const startIndex = currentSlide.value * 2;
  return latestImages.value.slice(startIndex, startIndex + 2);
});

// Navigation functions
const nextSlide = () => {
  currentSlide.value = (currentSlide.value + 1) % totalSlides.value;
  resetAutoplay();
};

const prevSlide = () => {
  currentSlide.value = (currentSlide.value - 1 + totalSlides.value) % totalSlides.value;
  resetAutoplay();
};

const goToSlide = (index) => {
  currentSlide.value = index;
  resetAutoplay();
};

// Autoplay functions
const startAutoplay = () => {
  if (totalSlides.value > 1) {
    autoplayInterval.value = setInterval(() => {
      currentSlide.value = (currentSlide.value + 1) % totalSlides.value;
    }, AUTOPLAY_DELAY);
  }
};

const stopAutoplay = () => {
  if (autoplayInterval.value) {
    clearInterval(autoplayInterval.value);
    autoplayInterval.value = null;
  }
};

const resetAutoplay = () => {
  stopAutoplay();
  startAutoplay();
};

const fetchArticles = async () => {
  try {
    const response = await axios.get(API_ENDPOINTS.newsLatestArticles);
    articles.value = response.data;
  } catch (error) {
    console.error("Error fetching articles:", error);
  }
};

const fetchLatestImages = async () => {
  try {
    const response = await axios.get(`${API_ENDPOINTS.latest}?limit=15`);
    latestImages.value = response.data;
    // Start autoplay after images are loaded
    startAutoplay();
  } catch (error) {
    console.error("Error fetching latest images:", error);
  }
};

onMounted(() => {
  fetchArticles();
  fetchLatestImages();
});

onUnmounted(() => {
  stopAutoplay();
});
</script>

<style lang="scss" scoped>
.opener{
  background-image: url("../assets/heroImage.webp");
  background-color: rgb(255 150 0);
  background-position: 50% 50%;
  background-size: cover;
  background-blend-mode: multiply;
}

.slider-container {
  position: relative;
}

.slider {
  align-items: center;
  justify-content: center;
  height: auto;
  overflow: hidden;
}

.slider-card-mobile {
  display: flex;
  flex-direction: column;
  width: calc(100% - 32px);
  max-width: 400px;
  margin: 0 auto;
  text-decoration: none;

  @media (min-width: 768px) {
    display: none !important;
  }
}

.slider-card-image-mobile {
  width: 100%;
  aspect-ratio: 16 / 10;
  background-color: rgba(255, 255, 255, 0.3);
  -webkit-backdrop-filter: blur(3px);
  backdrop-filter: blur(3px);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;

  img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }
}

.slider-card-description-mobile {
  color: rgba(255, 255, 255, 0.85);
  font-size: 0.9rem;
  margin: 0 0 10px 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.4;
}

.slider-desktop {
  display: none;

  @media (min-width: 768px) {
    display: flex;
  }
}

.slider-card {
  display: flex;
  flex-direction: column;
  width: calc(50% - 24px);
  max-width: 480px;
  text-decoration: none;
  transition: transform 0.3s ease;

  &:hover {
    transform: translateY(-4px);
  }
}

.slider-card-image {
  width: 100%;
  aspect-ratio: 16 / 10;
  background-color: rgba(255, 255, 255, 0.3);
  -webkit-backdrop-filter: blur(3px);
  backdrop-filter: blur(3px);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;

  img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }
}

.slider-card-content {
  background-color: #001c41;
  padding: 12px 16px;
  text-align: left;
}

.slider-card-title {
  color: white;
  font-weight: bold;
  font-size: 1.1rem;
  margin: 0 0 6px 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.3;
}

.slider-card-description {
  color: rgba(255, 255, 255, 0.85);
  font-size: 0.9rem;
  margin: 0 0 10px 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.4;

  @media (max-width: 1000px) {
    display: none;
  }
}

.slider-card-button {
  display: inline-block;
  background-color: #ff9600;
  color: white;
  font-weight: bold;
  padding: 4px 12px;
  font-size: 0.9rem;
  transition: background-color 0.2s ease;

  .group:hover & {
    background-color: #e68600;
  }
}

.slider-nav {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.9);
  color: #001c41;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  z-index: 10;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);

  &:hover {
    background: #ff9600;
    color: white;
  }

  &--prev {
    left: 10px;

    @media (min-width: 768px) {
      left: -20px;
    }
  }

  &--next {
    right: 10px;

    @media (min-width: 768px) {
      right: -20px;
    }
  }
}

.slider-dots {
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-top: 16px;
  padding-bottom: 8px;
}

.slider-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.5);
  border: 2px solid rgba(255, 255, 255, 0.8);
  cursor: pointer;
  transition: all 0.2s ease;
  padding: 0;

  &:hover {
    background: rgba(255, 255, 255, 0.8);
  }

  &--active {
    background: #ff9600;
    border-color: #ff9600;
    transform: scale(1.2);
  }
}

</style>
